/**
 * ============================================================================
 * CONTEXT: TourContext.tsx
 * ============================================================================
 * 
 * Context globale per la gestione dei tour interattivi guidati.
 * Utilizza driver.js come motore per i tour step-by-step.
 * 
 * FUNZIONALITÀ:
 * - Avviare tour predefiniti per nome (dashboard, course, admin)
 * - Gestire lo stato attivo/inattivo del tour
 * - Tracciare i tour già visualizzati (localStorage)
 * - Auto-start tour al primo accesso
 * 
 * UTILIZZO:
 * ```tsx
 * const { startTour, hasSeenTour } = useTour();
 * 
 * // Avvia tour con controllo automatico "già visto"
 * startTour('dashboard');
 * 
 * // Forza riavvio anche se già visto
 * startTour('dashboard', true);
 * ```
 * 
 * @author CCFN Development Team
 * @version 2.0.0 - Refactoring con configurazione centralizzata
 * @since 2026-01-20
 * ============================================================================
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { driver, DriveStep, Config } from 'driver.js';
import 'driver.js/dist/driver.css';
import { 
  TOUR_REGISTRY, 
  hasSeenTour as checkSeenTour, 
  markTourAsSeen,
  resetAllTours as resetStorage
} from '@/config/tourSteps';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Definizione di uno step del tour
 */
export interface TourStep {
  /** Selettore CSS dell'elemento (preferibilmente data-tour-id) */
  element: string;
  /** Configurazione del popover */
  popover: {
    title: string;
    description: string;
    side?: 'top' | 'bottom' | 'left' | 'right';
    align?: 'start' | 'center' | 'end';
  };
}

/**
 * Valore del context
 */
interface TourContextValue {
  /** Avvia un tour per ID. Se force=true, ignora il flag "già visto" */
  startTour: (tourId: string, force?: boolean) => void;
  /** Avvia un tour da definizione custom */
  startCustomTour: (steps: TourStep[]) => void;
  /** Ferma il tour attivo */
  stopTour: () => void;
  /** Indica se un tour è attivo */
  isActive: boolean;
  /** ID del tour attualmente attivo */
  activeTourId: string | null;
  /** Verifica se un tour è già stato visualizzato */
  hasSeenTour: (tourId: string) => boolean;
  /** Resetta tutti i tour (per riavviarli) */
  resetAllTours: () => void;
  /** Lista dei tour disponibili */
  availableTours: string[];
  /** Determina se usare tour mobile */
  isMobile: boolean;
}

// ============================================================================
// CONTEXT
// ============================================================================

const TourContext = createContext<TourContextValue | undefined>(undefined);

// ============================================================================
// CONFIGURAZIONE DRIVER.JS
// ============================================================================

/**
 * Configurazione di default per driver.js
 * Stili personalizzati per match col design system LNI
 */
const defaultDriverConfig: Partial<Config> = {
  showProgress: true,
  showButtons: ['next', 'previous', 'close'],
  nextBtnText: 'Avanti',
  prevBtnText: 'Indietro',
  doneBtnText: 'Fine',
  progressText: '{{current}} di {{total}}',
  animate: true,
  allowClose: true,
  overlayColor: 'rgba(0, 0, 0, 0.7)',
  stagePadding: 10,
  stageRadius: 8,
  popoverClass: 'lni-tour-popover',
};

// ============================================================================
// PROVIDER
// ============================================================================

interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  // ----------------------------------------
  // State
  // ----------------------------------------
  const [isActive, setIsActive] = useState(false);
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // ----------------------------------------
  // Effetti
  // ----------------------------------------

  /**
   * Rileva se siamo su mobile per tour ottimizzati
   */
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ----------------------------------------
  // Handlers
  // ----------------------------------------

  /**
   * Converte i nostri TourStep in DriveStep per driver.js
   */
  const convertToDriverSteps = useCallback((steps: TourStep[]): DriveStep[] => {
    return steps.map((step) => ({
      element: step.element,
      popover: {
        title: step.popover.title,
        description: step.popover.description,
        side: step.popover.side,
        align: step.popover.align,
      },
    }));
  }, []);

  /**
   * Avvia un tour custom da una lista di step
   */
  const startCustomTour = useCallback((steps: TourStep[]) => {
    const driverInstance = driver({
      ...defaultDriverConfig,
      steps: convertToDriverSteps(steps),
      onDestroyStarted: () => {
        setIsActive(false);
        setActiveTourId(null);
        driverInstance.destroy();
      },
    });

    setIsActive(true);
    driverInstance.drive();
  }, [convertToDriverSteps]);

  /**
   * Avvia un tour registrato per ID
   * @param tourId - ID del tour da avviare
   * @param force - Se true, ignora il flag "già visto"
   */
  const startTour = useCallback((tourId: string, force: boolean = false) => {
    // Verifica se il tour esiste
    const tourConfig = TOUR_REGISTRY[tourId];
    if (!tourConfig) {
      console.warn(`[TourManager] Tour "${tourId}" non trovato nel registro`);
      return;
    }

    // Se non forzato, controlla se già visto
    if (!force && checkSeenTour(tourId)) {
      console.log(`[TourManager] Tour "${tourId}" già visualizzato`);
      return;
    }

    // Seleziona tour mobile se appropriato
    const actualTourId = isMobile && TOUR_REGISTRY[`${tourId}-mobile`] 
      ? `${tourId}-mobile` 
      : tourId;
    const actualTour = TOUR_REGISTRY[actualTourId] || tourConfig;

    const driverInstance = driver({
      ...defaultDriverConfig,
      steps: convertToDriverSteps(actualTour.steps),
      onDestroyStarted: () => {
        // Marca come visto quando il tour termina
        markTourAsSeen(tourId);
        if (actualTourId !== tourId) {
          markTourAsSeen(actualTourId);
        }
        setIsActive(false);
        setActiveTourId(null);
        driverInstance.destroy();
      },
    });

    setActiveTourId(tourId);
    setIsActive(true);
    
    // Piccolo delay per assicurarsi che gli elementi siano renderizzati
    setTimeout(() => {
      driverInstance.drive();
    }, 300);
  }, [convertToDriverSteps, isMobile]);

  /**
   * Ferma il tour attivo
   */
  const stopTour = useCallback(() => {
    setIsActive(false);
    setActiveTourId(null);
  }, []);

  /**
   * Verifica se un tour è già stato visto
   */
  const hasSeenTour = useCallback((tourId: string) => {
    return checkSeenTour(tourId);
  }, []);

  /**
   * Resetta tutti i tour
   */
  const resetAllTours = useCallback(() => {
    resetStorage();
  }, []);

  // ----------------------------------------
  // Context Value
  // ----------------------------------------
  const availableToursList = useMemo(() => Object.keys(TOUR_REGISTRY), []);

  const value = useMemo<TourContextValue>(() => ({
    startTour,
    startCustomTour,
    stopTour,
    isActive,
    activeTourId,
    hasSeenTour,
    resetAllTours,
    availableTours: availableToursList,
    isMobile,
  }), [startTour, startCustomTour, stopTour, isActive, activeTourId, hasSeenTour, resetAllTours, availableToursList, isMobile]);

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook per accedere al TourContext
 * @throws Error se usato fuori dal TourProvider
 */
export function useTour(): TourContextValue {
  const context = useContext(TourContext);
  
  if (!context) {
    throw new Error('useTour deve essere usato all\'interno di un TourProvider');
  }
  
  return context;
}

export default TourContext;
